import { useEffect } from "react";
import { Container, Card } from "react-bootstrap";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../css/Legal.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "JACAi – Privacy Policy";
  }, []);

  return (
    <>
      <Header />

      <div className="legal-page">
        <Container className="legal-container py-5">
          <Card className="legal-card">
            <div className="legal-card-inner">
              <h1 className="mb-2">Privacy Policy</h1>
              <p className="text-muted mb-4">
                <strong>Last Updated:</strong> November 14, 2025
              </p>

              <h4 className="mt-4">1. Introduction</h4>
              <p>
                This Privacy Policy explains how I, <strong>Marius Cirjan</strong>, collect, use,
                and protect data when you use <strong>JACAi</strong> (the “Service”), an AI chatbot
                that also provides optional features such as <strong>Resume Analyzer</strong> and
                <strong> CV (resume) generation</strong>. I comply with the General Data Protection
                Regulation (GDPR) and applicable Romanian/EU data protection laws.
              </p>

              <h4 className="mt-4">2. Data Controller</h4>
              <p>
                <strong>Data Controller:</strong> Marius Cirjan <br />
                <strong>Email:</strong>{" "}
                <a href="mailto:cirjan.marius7@gmail.com">cirjan.marius7@gmail.com</a>
              </p>

              <h4 className="mt-4">3. Data We Collect</h4>

              <h5 className="mt-3">3.1 Chat Data (General Use)</h5>
              <p>
                The Service does not require a personal account. Authentication is performed only
                through <strong>Firebase Anonymous Sign-In</strong>, which creates a random
                anonymous user ID.
              </p>
              <p className="mb-1">
                <strong>We may collect and store:</strong>
              </p>
              <ul>
                <li>Messages you send to the chatbot</li>
                <li>AI-generated responses</li>
                <li>Conversation thread titles and timestamps</li>
                <li>A random anonymous user ID generated by Firebase</li>
                <li>Usage counters (e.g., daily message limits)</li>
              </ul>

              <h5 className="mt-3">3.2 Resume Analyzer (Optional Feature)</h5>
              <p>
                If you choose to use the <strong>Resume Analyzer</strong> feature, you may submit
                resume/CV content (text and/or files) that can contain personal data, such as your
                name, contact details, work history, education, or links.
              </p>
              <p className="mb-1">
                <strong>For Resume Analyzer, we may process:</strong>
              </p>
              <ul>
                <li>Resume/CV text you paste into the Service</li>
                <li>Feedback requests, questions, and your follow-up messages</li>
                <li>AI-generated analysis, suggestions, and scoring (if provided)</li>
              </ul>
              <p>
                By default, analyzer inputs and outputs may be stored in your conversation thread
                to maintain history and allow you to revisit the analysis.
              </p>

              <h5 className="mt-3">3.3 CV / Resume Generation (Optional Feature)</h5>
              <p>
                If you choose to use the CV generation feature, you may voluntarily provide personal
                data to build a CV draft (for example: name, email, phone, location, links, experience,
                education, projects, certifications, languages, and summary).
              </p>
              <p>
                This CV data can be stored as part of your conversation thread (e.g., as a CV draft)
                so you can continue editing and generate the PDF again.
              </p>

              <h4 className="mt-4">4. Purpose of Processing</h4>
              <p>Your data is used to:</p>
              <ul>
                <li>Provide the chatbot functionality and maintain conversation history</li>
                <li>Support multiple conversation threads</li>
                <li>Enforce fair-usage limits and prevent abuse</li>
                <li>Provide Resume Analyzer feedback and improvements</li>
                <li>Provide the CV generation feature and generate a downloadable PDF</li>
                <li>Ensure safety, security, and stability of the Service</li>
              </ul>

              <h4 className="mt-4">5. Legal Basis (GDPR Article 6)</h4>
              <ul>
                <li>
                  <strong>Art. 6(1)(b)</strong> – Processing necessary to provide the Service and
                  the features you request (including Resume Analyzer and CV generation).
                </li>
                <li>
                  <strong>Art. 6(1)(f)</strong> – Legitimate interest (security, abuse prevention,
                  service reliability).
                </li>
              </ul>

              <h4 className="mt-4">6. Authentication</h4>
              <p>
                The Service uses <strong>Firebase Authentication (Anonymous Sign-In)</strong>.
                Firebase assigns an anonymous identifier so your chat threads remain consistent
                across sessions. This identifier does not contain your real name, email, or password.
              </p>

              <h4 className="mt-4">7. Data Storage and Hosting</h4>
              <p>
                Chat data, Resume Analyzer inputs/outputs, and CV drafts are stored in{" "}
                <strong>Google Firebase Firestore</strong>. Firebase infrastructure may be located
                within or outside the EU. Where applicable, transfers are protected by GDPR-compliant
                safeguards such as Standard Contractual Clauses (SCCs).
              </p>

              <h4 className="mt-4">8. Data Sharing</h4>
              <p>Your data is shared only with:</p>
              <ul>
                <li>Google Firebase (authentication and storage)</li>
                <li>
                  AI/model providers used to generate responses and analysis (for example, a local
                  model runtime such as Ollama, depending on your deployment). Only the content required
                  to fulfill your request is processed.
                </li>
                <li>Authorities only if legally required</li>
              </ul>
              <p>
                We do not sell your data and do not use your chat/resume content for advertising.
              </p>

              <h4 className="mt-4">9. Data Retention</h4>
              <p>
                We retain data only as long as needed to provide the Service. Chat messages, Resume
                Analyzer content, and CV drafts may be stored to preserve conversation history and allow
                you to revisit results, continue editing, and regenerate documents.
              </p>
              <p>
                You can request deletion of your stored chat history, analyzer data, and/or CV data at
                any time by contacting the email address in the Contact section.
              </p>

              <h4 className="mt-4">10. Your Rights (GDPR)</h4>
              <p>You have the right to:</p>
              <ul>
                <li>Access your data</li>
                <li>Rectify inaccurate data</li>
                <li>Request erasure (“right to be forgotten”)</li>
                <li>Restrict processing</li>
                <li>Object to processing</li>
                <li>Data portability (where applicable)</li>
              </ul>
              <p>
                To exercise your rights, contact me using the email address below. For security, I may
                ask for reasonable information to confirm your request relates to your data.
              </p>

              <h4 className="mt-4">11. Children</h4>
              <p>
                This Service is not intended for users under the age of 16. If you are a parent or
                guardian and believe data has been submitted by a minor, please contact me.
              </p>

              <h4 className="mt-4">12. Security</h4>
              <p>
                I implement reasonable technical and organizational measures to protect data, including
                Firestore security rules, access controls, and secure communication. No method of
                transmission or storage is 100% secure, but I work to protect your data.
              </p>

              <h4 className="mt-4">13. Cookies</h4>
              <p>
                The Service may use essential cookies or local storage mechanisms required for
                functionality (for example, to support sessions or remember preferences). We do not use
                cookies for advertising.
              </p>

              <h4 className="mt-4">14. Updates to This Policy</h4>
              <p>
                This Privacy Policy may be updated from time to time. Changes will be posted on this
                page and indicated by the “Last Updated” date.
              </p>

              <h4 className="mt-4">15. Contact</h4>
              <p>
                <strong>Marius Cirjan</strong> <br />
                <a href="mailto:cirjan.marius7@gmail.com">cirjan.marius7@gmail.com</a>
              </p>
            </div>
          </Card>
        </Container>
      </div>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;